import './AuthContextConfig';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { client } from '../../apollo/ApolloConfig';
import { clearPendingTimeouts } from '../../apollo/timeout/timeoutManager';
import {
  kanbanJobTitleId,
  kanbanProficiencyLevel,
  kanbanSkillType,
  loadingWizard,
  occupationPreferenceId,
  userRoleVar,
} from '../../cache/cache';
import { majorIdVar } from '../../cache/common/majorIdReactiveVar';
import { renderNotification } from '../../cache/learner/homeReactiveVars';
import { ikigaiJobsVar } from '../../cache/learner/ikigaiJobsReactiveVar';
import { emptyJobPreferencesCache, jobPreferencesVar } from '../../cache/learner/jobPreferencesReactiveVar';
import { kanbanDataVar } from '../../cache/learner/kanbanReactiveVar';
import AtomProgress from '../../components/atoms/AtomProgress';
import { styleConfigs } from '../../components/resources/styleConfigs';
import BoxWrapper from '../../components/wrappers/BoxWrapper';
import { FeaturesEnum } from '../../constants/enums/FeaturesEnum';
import useKanbanDataHook from '../../hooks/kanbanDataHook/useKanbanDataHook';
import { useDefaultStudentStartPage } from '../../hooks/useHomepagePath';
import useLogger from '../../hooks/useLogger/useLogger';
import useSetDataFromLocalStorage from '../../hooks/useSetDataFromLocalStorage/useSetDataFromLocalStorage';
import { Mixpanel } from '../../mixpanel/mixpanel';
import { useLazyFeatureComplete } from '../../operations/query/feature/useGetFeatureComplete';
import { useQueryGetFeatureFlags } from '../../operations/query/public/features/useGetFeatureFlags';
import {
  IFeatureWithValue,
  useQueryGetFeaturesWithValue,
} from '../../operations/query/public/features/useGetFeaturesWithValues';
import { IGetLearnerEntireProfile } from '../../operations/query/students/useGetLearnerEntireProfile/useGetLearnerEntireProfile';
import { PATH_DASHBOARD, PATH_WIZARD } from '../../routes/paths';
import { getCookie, removeCookie, setCookie } from '../../utils/cookiesUtil';
import { decryptor } from '../../utils/decryptor';
import { handleCaseAuth } from '../../utils/handleCaseAuth';
import { tokenDecode } from '../../utils/jwtUtils';
import { clearWhiteLabelCache, readWhiteLabelCache, writeWhiteLabelCache } from '../../utils/whiteLabelCache';
import { ILoginUserSuccessResponse, ROLE_ENUM } from '../../views/Auth/Login/interfaces/IGoogleUser';
import { FeatureFlagsContext } from '../FeatureFlagsContext';
import { IUserAuth } from '../reducer/userAuthReducer';

interface IAuthContextProviderProps {
  children: ReactNode;
  dispatch: React.Dispatch<{
    type: string;
    initialValue: string[];
    features?: IFeatureWithValue[];
  }>;
}

const userAuthInitialState: IUserAuth = {
  auth: {
    refreshToken: '',
    expiresIn: 0,
    remember: false,
    username: '',
    uid: '',
    displayName: '',
    onBoardingCompleted: false,
    email: '',
    student: {
      id: '',
      email: '',
      name: '',
      lastName: '',
      studentType: '',
    },
    studentInstitution: {
      institutionId: '',
      institutionName: '',
      otherInstitutionName: '',
    },
    userRole: '',
    jwtToken: '',
    features: [],
    preferredLanguage: '',
    specializedMajor: false,
  },
  logout: async () => new Promise((resolve) => resolve()),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  setToken: (_: ILoginUserSuccessResponse, expiresIn: number, jwtToken?: string) => {},
};

const AuthContext = createContext<IUserAuth>(userAuthInitialState);

const AuthContextProvider = ({ children, dispatch }: IAuthContextProviderProps): JSX.Element | null => {
  const { t } = useTranslation('AuthContext');
  const location = useLocation();
  const navigate = useNavigate();
  const [auth, setAuth] = useState<ILoginUserSuccessResponse | undefined | null>(undefined);
  const [learnerEntireProfile, setLearnerEntireProfile] = useState<IGetLearnerEntireProfile | undefined>(undefined);
  const [learnerProfileLoading, setLearnerProfileLoading] = useState(false);
  const { features, setFeatures } = useContext(FeatureFlagsContext);
  const { i18n } = useTranslation();
  const { data: featuresData, loading } = useQueryGetFeatureFlags(false);
  const { data: featuresDataWithValues } = useQueryGetFeaturesWithValue(false);
  const { operations } = useKanbanDataHook(kanbanDataVar, kanbanSkillType, kanbanJobTitleId, kanbanProficiencyLevel);
  const { operations: localStorageOperations } = useSetDataFromLocalStorage();
  const defaultStudentStartPage = useDefaultStudentStartPage();
  const { operations: loggerOperations } = useLogger();
  // Whether `featuresData` has been applied to FeatureFlagsContext yet (avoids releasing the
  // loader one render before `features.qsemDesignWhiteLabel` reflects the fetched flags).
  const [featuresApplied, setFeaturesApplied] = useState(false);
  const [cachedWhiteLabel] = useState(() => readWhiteLabelCache());
  const [whiteLabelFetchTriggered, setWhiteLabelFetchTriggered] = useState(false);
  const [getWhiteLabelFeature, { data: whiteLabelFeatureData, loading: whiteLabelLoading }] =
    useLazyFeatureComplete(false);
  const isAuthRoute = location.pathname.includes('auth');
  const needsWhiteLabelFetch = isAuthRoute && Boolean(features.qsemDesignWhiteLabel) && !cachedWhiteLabel;
  const waitingForWhiteLabel = needsWhiteLabelFetch && (!whiteLabelFetchTriggered || whiteLabelLoading);

  const commonAuthLogout = (): void => {
    removeCookie('token');
    removeCookie('preferredLanguage');
    clearWhiteLabelCache();
    const redirectOnLogout = features.qsemLogoutImmediateRedirect?.url;

    setAuth(null);

    if (redirectOnLogout) {
      const isAbsolute = redirectOnLogout.startsWith('http');

      globalThis.location.href = isAbsolute ? redirectOnLogout : `${globalThis.location.origin}${redirectOnLogout}`;
    }
  };

  const getStudentPath = (isCompleteStudentInfo: boolean): string =>
    isCompleteStudentInfo ? defaultStudentStartPage : PATH_WIZARD.root;

  const handledAuth = async (): Promise<void> => {
    const token = getCookie('token');

    let user: ILoginUserSuccessResponse | undefined;

    if (token) {
      try {
        user = tokenDecode(token);
      } catch (error: unknown) {
        loggerOperations.writeLogger({
          project: 'front-end',
          source: 'AuthContext',
          func: 'handledAuth',
          error: JSON.stringify(error),
          level: 'error',
        });

        return;
      }
    }
    const caseToEvaluate = handleCaseAuth({ user, token });

    switch (caseToEvaluate) {
      case 'jwtTokenAndTokenAndNotExpired':
        dispatch({ type: 'addFeatures', initialValue: user?.features ?? [] });
        setAuth(user);
        if (location.pathname === '/') {
          if (user?.userRole === ROLE_ENUM.ADMIN) {
            navigate(PATH_DASHBOARD.admin.pageInsights);
          } else {
            navigate(getStudentPath(!!user?.onBoardingCompleted));
          }
        }
        break;

      case 'tokenAndExpiredTokenAndredirectLogoutUrl':
        commonAuthLogout();
        globalThis.location.href = user?.redirectLogoutUrl ?? globalThis.location.href;
        break;

      default:
        commonAuthLogout();
    }
  };

  useEffect(() => {
    handledAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (auth) {
      dispatch({ type: 'addFeatures', initialValue: auth?.features ?? [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const logout = async (): Promise<void> => {
    Mixpanel.reset();
    const redirectOnLogout = features.qsemLogoutImmediateRedirect?.url;

    setAuth(null);
    userRoleVar('');
    localStorageOperations.deleteLocalStorage();
    clearWhiteLabelCache();
    clearPendingTimeouts();
    loadingWizard(false);
    ikigaiJobsVar({ error: undefined, data: [] });
    renderNotification({
      newNotifications: [],
      beforeNotifications: [],
    });
    majorIdVar(undefined);
    occupationPreferenceId(undefined);
    jobPreferencesVar(emptyJobPreferencesCache);
    operations.setKanbanData([]);
    await client.clearStore();

    if (redirectOnLogout) {
      const isAbsolute = redirectOnLogout.startsWith('http');

      globalThis.location.href = isAbsolute ? redirectOnLogout : `https://${redirectOnLogout}`;
    }
  };

  const setToken = (token: ILoginUserSuccessResponse, expiresIn: number, jwtToken?: string): void => {
    setAuth({
      ...token,
      jwtToken: token.jwtToken ?? jwtToken ?? '',
    });

    if (jwtToken) {
      setCookie('token', jwtToken);
    }
  };

  useEffect(() => {
    let lang;

    if (auth) {
      lang =
        getCookie('preferredLanguage') ??
        auth?.preferredLanguage ??
        features?.qsemLangDefaultLanguage?.language ??
        'en';
    } else {
      lang = getCookie('preferredLanguage') ?? features?.qsemLangDefaultLanguage?.language ?? 'en';
    }

    i18n.changeLanguage(lang);

    if (auth) {
      setCookie('preferredLanguage', lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, auth]);

  useEffect(() => {
    const token = getCookie('token');
    const loginAs = decryptor(sessionStorage.getItem('loginAs') ?? undefined);

    const userAuth = token ? tokenDecode(token) : undefined;

    if (loginAs) {
      userRoleVar(loginAs ?? '');
    } else {
      userRoleVar(userAuth?.userRole ?? '');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (featuresData) {
      setFeatures(featuresData.features);
      setFeaturesApplied(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuresData]);

  useEffect(() => {
    if (needsWhiteLabelFetch && !whiteLabelFetchTriggered) {
      setWhiteLabelFetchTriggered(true);
      getWhiteLabelFeature({ variables: { feature: FeaturesEnum.qsemDesignWhiteLabel } });
    }
  }, [needsWhiteLabelFetch, whiteLabelFetchTriggered, getWhiteLabelFeature]);

  useEffect(() => {
    const customTheme = whiteLabelFeatureData?.getFeatureNameAndValue
      ? JSON.parse(whiteLabelFeatureData.getFeatureNameAndValue.featureValue)
      : null;

    if (customTheme) {
      writeWhiteLabelCache(customTheme);
    }
  }, [whiteLabelFeatureData]);

  useEffect(() => {
    if (featuresData && featuresDataWithValues) {
      dispatch({
        type: 'addFeaturesWithValues',
        initialValue: [],
        features: featuresDataWithValues.getFeaturesWithValue,
      });
    }
  }, [featuresDataWithValues, featuresData, dispatch, auth]);

  const authData: any = useMemo(
    () => ({
      auth,
      logout,
      setToken,
      learnerEntireProfile,
      setLearnerEntireProfile,
      learnerProfileLoading,
      setLearnerProfileLoading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth, learnerEntireProfile, learnerProfileLoading],
  );

  const showLoader = loading || !featuresApplied || waitingForWhiteLabel;

  if (showLoader) {
    return (
      <BoxWrapper sx={{ position: 'relative', height: styleConfigs.dimensions.height100vh }}>
        {isAuthRoute && (
          <AtomProgress
            visible={showLoader}
            layout="overlay"
            iconVariant="light"
            loadingText={t('LOADER_TEXT')}
            textColor="white"
          />
        )}
      </BoxWrapper>
    );
  }

  return <AuthContext.Provider value={authData}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthContextProvider };
